import { AfterViewInit, Component, computed, inject, viewChild } from '@angular/core';
import { EditorComponent, EditorCssSettings } from '@progress/kendo-angular-editor';
import { Observable, delay, of } from 'rxjs';
import { documentThemeStylesPath } from './edit-document-section-wysiwyg.config';
import { take } from 'rxjs/operators';
import { editorPlugins } from '../../../../../../shared/text-editor/plugins/wysiwyg-editor-plugins';
import { editorFontFamilies } from '../../../../../../shared/text-editor/configuration';
import { EditDocumentAreaService } from '../../../../../../core/services/features/document-editing/edit-document-area.service';
import { InvoiceStateService } from '../../state-and-data-handling/invoice-state.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { customSchema } from './edit-document-section-wysiwyg.constants';

@Component({
  selector: 'app-edit-document-section-wysiwyg',
  standalone: false,
  templateUrl: './edit-document-section-wysiwyg.component.html',
})
export class EditDocumentSectionWysiwygComponent implements AfterViewInit {
  // Signals and viewChild
  editor = viewChild<EditorComponent>('editor');
  // Automatically recomputes when `selected()` or `contentAreas()` change
  contentElement = computed<string | null>(() => {
    const selected = this.editDocumentAreaService.selected();
    const areas = this.invoiceStateService.contentAreas();
    const id = selected.sectionId;
    if (!id) {
      return null;
    }

    return areas.get(id)?.content ?? null;
  });

  public editorPlugins = editorPlugins;
  public iframeStyles: EditorCssSettings = {
    path: `${documentThemeStylesPath}/xemedo-theme-styles.scss`, // Todo: rename xemedo
  };
  public readonly editorFontFamilies = editorFontFamilies;
  public readonly customSchema = customSchema;

  // DI
  private readonly editDocumentAreaService = inject(EditDocumentAreaService);
  private readonly invoiceStateService = inject(InvoiceStateService);

  constructor() {
    toObservable(this.invoiceStateService.meta).subscribe(() => {
      const editorHostEl = this.editor()?.view?.dom;
      if (editorHostEl) {
        this.editDocumentAreaService.fillDynamicFields(this.invoiceStateService.meta(), {
          nativeElement: editorHostEl,
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.waitForEditorRender()
      .pipe(take(1))
      .subscribe(() => {
        if (this.editor()?.view) {
          this.editor().exec('fontFamily', 'calibriregular, sans-serif');
          this.editor().exec('fontSize', '14px');
        }

        // Replace placeholder.
        const editorHostEl = this.editor().view?.dom;
        if (editorHostEl) {
          this.editDocumentAreaService.fillDynamicFields(this.invoiceStateService.meta(), {
            nativeElement: editorHostEl,
          });
        }
      });
  }

  public toEditorHtml(html: string): string {
    return html.replace(
      /<span\s+data-dynamic-field-meta-key="([^"]*)"\s*><\/span>/gi,
      '<dynamic-field data-dynamic-field-meta-key="$1"></dynamic-field>',
    );
  }

  public contentChanged(html: string): void {
    if (html === null) {
      return;
    }

    const contentAreaName = this.editDocumentAreaService.selected().sectionId;
    this.invoiceStateService.updateContentArea(contentAreaName, { content: html });
  }

  private waitForEditorRender(): Observable<void> {
    return of(null).pipe(delay(100));
  }
}
